// Module unlock and weekly cadence logic
// Hybrid: calendar-based (one module per week from first start)
//         OR completion-based (finish previous to unlock early)

export const MODULE_ORDER = [
  'quest',
  'algorithm',
  'dataheist',
  'phantom',
  'permissions',
  'trial',
  'leaklab',
  'detective',
] as const;

export type ModuleId = typeof MODULE_ORDER[number];

const PROGRESS_KEY = 'pe_public_progress';
const DEMO_KEY = 'pe_public_demo_mode';
const WEEK_MS = 7 * 24 * 60 * 60 * 1000;

interface ProgressState {
  startedAt: number | null;
}

function loadProgress(): ProgressState {
  try {
    const raw = localStorage.getItem(PROGRESS_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return { startedAt: null };
}

function saveProgress(s: ProgressState): void {
  localStorage.setItem(PROGRESS_KEY, JSON.stringify(s));
}

// ── Demo mode (super admin only) ──────────────────────────────────────────────

export function isDemoMode(): boolean {
  return localStorage.getItem(DEMO_KEY) === '1';
}

export function setDemoMode(on: boolean): void {
  if (on) localStorage.setItem(DEMO_KEY, '1');
  else localStorage.removeItem(DEMO_KEY);
}

// ── Start tracking ─────────────────────────────────────────────────────────────

/** Call once when the user first starts (earns XP). Auto-skips if already set. */
export function recordStart(): void {
  const p = loadProgress();
  if (!p.startedAt) {
    saveProgress({ startedAt: Date.now() });
  }
}

/** Returns the unix timestamp (ms) when the user first started, or null. */
export function getStartedAt(): number | null {
  return loadProgress().startedAt;
}

/**
 * Is module at `index` unlocked?
 *
 * Module 0 is always unlocked.
 * Module N (N > 0) unlocks when either:
 *   - the previous module is in completedModules (early-unlock by completion), OR
 *   - startedAt + N weeks has passed (calendar unlock)
 */
export function isUnlocked(moduleIndex: number, completedModules: string[]): boolean {
  if (isDemoMode()) return true;
  if (moduleIndex === 0) return true;
  // Completion-based early unlock: previous module done
  const prevId = MODULE_ORDER[moduleIndex - 1];
  if (completedModules.some(m => m.startsWith(prevId))) return true;
  // Calendar unlock
  const { startedAt } = loadProgress();
  if (!startedAt) return false;
  return Date.now() >= startedAt + moduleIndex * WEEK_MS;
}

/**
 * Returns the index into MODULE_ORDER of "this week's module":
 * the latest unlocked module that is not yet completed.
 * Falls back to the latest unlocked module if all unlocked ones are done.
 */
export function getThisWeekIndex(completedModules: string[]): number {
  const { startedAt } = loadProgress();
  // Before they start, default to module 0
  if (!startedAt) return 0;

  let lastUnlockedIdx = 0;
  for (let i = 0; i < MODULE_ORDER.length; i++) {
    if (isUnlocked(i, completedModules)) lastUnlockedIdx = i;
    else break;
  }

  // First incomplete unlocked module
  for (let i = 0; i <= lastUnlockedIdx; i++) {
    const id = MODULE_ORDER[i];
    if (!completedModules.some(m => m.startsWith(id))) return i;
  }

  return lastUnlockedIdx;
}

/** How many days until module N unlocks via the calendar (0 if already unlocked). */
export function daysUntilUnlock(moduleIndex: number, completedModules: string[]): number {
  if (isUnlocked(moduleIndex, completedModules)) return 0;
  const { startedAt } = loadProgress();
  if (!startedAt) return moduleIndex * 7;
  const unlockAt = startedAt + moduleIndex * WEEK_MS;
  return Math.ceil((unlockAt - Date.now()) / (24 * 60 * 60 * 1000));
}
